import { CloudUpload, InsertDriveFile } from "@mui/icons-material";
import {
	Alert,
	Box,
	Button,
	FormControlLabel,
	Grid2,
	LinearProgress,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../components/modals/error-modal";
import MesfModal from "../../../../components/modals/modal.mesf";
import { ButtonWithLoading } from "../../../../components/shared/buttons/button-with-loading";
import type { AppRelease, AppReleaseApp } from "../models/AppRelease";
import {
	compareVersions,
	formatFileSize,
	isValidVersion,
	parseApkFileName,
} from "../models/versions";
import { uploadAppRelease } from "../repositories/AppReleasesRepository";

type FormValues = {
	Version: string;
	Build: string;
	MinSupportedVersion: string;
	ReleaseNotes: string;
	IsActive: boolean;
};

const INITIAL_VALUES: FormValues = {
	Version: "",
	Build: "",
	MinSupportedVersion: "",
	ReleaseNotes: "",
	IsActive: false,
};

type Props = {
	open: boolean;
	onHide: (uploaded: boolean) => void;
	app: AppReleaseApp;
	activeRelease: AppRelease | null;
};

export const UploadReleaseModal = ({
	open,
	onHide,
	app,
	activeRelease,
}: Props) => {
	const queryClient = useQueryClient();

	const [file, setFile] = useState<File | null>(null);
	const [fileError, setFileError] = useState("");
	const [progress, setProgress] = useState(0);
	const [error, setError] = useState("");

	const { control, reset, handleSubmit, setValue, watch } = useForm<FormValues>(
		{ defaultValues: INITIAL_VALUES },
	);

	const build = watch("Build");

	const uploadMutation = useMutation(uploadAppRelease, {
		onSuccess: async () => {
			await queryClient.invalidateQueries(["appReleases"]);
			onHide(true);
		},
		onError: (mutationError: Error) => {
			setProgress(0);
			setError(mutationError.message);
		},
	});

	const isUploading = uploadMutation.isLoading;

	useEffect(() => {
		if (open) {
			reset(INITIAL_VALUES);
			setFile(null);
			setFileError("");
			setProgress(0);
		}
	}, [open, reset]);

	const onDrop = useCallback(
		(acceptedFiles: File[]) => {
			const dropped = acceptedFiles[0];

			if (!dropped) {
				return;
			}

			if (!dropped.name.toLowerCase().endsWith(".apk")) {
				setFileError("Only .apk files are allowed");
				return;
			}

			setFileError("");
			setFile(dropped);

			const parsed = parseApkFileName(dropped.name);
			if (parsed) {
				setValue("Version", parsed.version, { shouldValidate: true });
				setValue("Build", String(parsed.build), { shouldValidate: true });
			}
		},
		[setValue],
	);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		multiple: false,
		disabled: isUploading,
	});

	const buildNumber = Number(build);
	const showBuildWarning =
		!!activeRelease &&
		build.trim() !== "" &&
		Number.isInteger(buildNumber) &&
		buildNumber <= activeRelease.Build;

	const onSubmit: SubmitHandler<FormValues> = (data) => {
		if (!file) {
			setFileError("Select the APK to upload");
			return;
		}

		setProgress(0);
		uploadMutation.mutate({
			file,
			appCode: app.appCode,
			platform: app.platform,
			version: data.Version.trim(),
			build: Number(data.Build),
			minSupportedVersion: data.MinSupportedVersion.trim() || null,
			releaseNotes: data.ReleaseNotes.trim() || null,
			isActive: data.IsActive,
			onProgress: setProgress,
		});
	};

	const onCancel = () => {
		if (isUploading) {
			return;
		}
		onHide(false);
	};

	return (
		<>
			<MesfModal
				title={`UPLOAD RELEASE - ${app.label}`}
				open={open}
				handleClose={onCancel}
				maxWidth="sm"
				id="upload-app-release-modal"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers style={{ padding: "10px 30px" }}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12 }}>
								<Box
									{...getRootProps()}
									sx={{
										border: (theme) =>
											`2px dashed ${
												fileError
													? theme.palette.error.main
													: isDragActive
														? theme.palette.secondary.main
														: theme.palette.primary.main
											}`,
										borderRadius: (theme) => theme.shape.borderRadius,
										padding: (theme) => theme.spacing(3),
										textAlign: "center",
										cursor: isUploading ? "default" : "pointer",
										backgroundColor: (theme) =>
											isDragActive
												? theme.palette.action.selected
												: theme.palette.background.default,
										"&:hover": {
											backgroundColor: (theme) => theme.palette.action.hover,
										},
									}}
								>
									<input {...getInputProps()} />
									{file ? (
										<>
											<InsertDriveFile sx={{ fontSize: 40 }} color="primary" />
											<Typography variant="subtitle1">{file.name}</Typography>
											<Typography variant="body2" color="textSecondary">
												{formatFileSize(file.size)} - click or drop to replace
											</Typography>
										</>
									) : (
										<>
											<CloudUpload sx={{ fontSize: 40 }} color="primary" />
											<Typography variant="subtitle1">
												{isDragActive
													? "Drop the APK here..."
													: "Drag & drop the APK here, or click to select"}
											</Typography>
											<Typography variant="body2" color="textSecondary">
												Version and build are filled in from CI file names like{" "}
												<code>swv-mill_3.48.0-146.apk</code>
											</Typography>
										</>
									)}
								</Box>
								{fileError ? (
									<Typography variant="body2" color="error" mt={0.5}>
										{fileError}
									</Typography>
								) : null}
							</Grid2>
							<Grid2 size={{ xs: 12, sm: 6 }}>
								<Controller
									name="Version"
									control={control}
									rules={{
										required: "Version is required",
										validate: (value) =>
											isValidVersion(value) || "Use the format 1.2.3",
									}}
									render={({ field, fieldState: { error: fieldError } }) => (
										<TextField
											{...field}
											label="Version"
											placeholder="3.48.0"
											fullWidth
											margin="dense"
											autoComplete="off"
											disabled={isUploading}
											error={!!fieldError}
											helperText={fieldError?.message}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, sm: 6 }}>
								<Controller
									name="Build"
									control={control}
									rules={{
										required: "Build is required",
										validate: (value) =>
											(/^\d+$/.test(value.trim()) && Number(value) > 0) ||
											"Build must be a positive whole number",
									}}
									render={({ field, fieldState: { error: fieldError } }) => (
										<TextField
											{...field}
											label="Build (versionCode)"
											placeholder="146"
											fullWidth
											margin="dense"
											autoComplete="off"
											disabled={isUploading}
											error={!!fieldError}
											helperText={fieldError?.message}
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, sm: 6 }}>
								<Controller
									name="MinSupportedVersion"
									control={control}
									rules={{
										validate: (value, values) => {
											const trimmed = value.trim();
											if (trimmed === "") {
												return true;
											}
											if (!isValidVersion(trimmed)) {
												return "Use the format 1.2.3";
											}
											if (
												isValidVersion(values.Version) &&
												compareVersions(trimmed, values.Version) > 0
											) {
												return "Cannot be higher than the version";
											}
											return true;
										},
									}}
									render={({ field, fieldState: { error: fieldError } }) => (
										<TextField
											{...field}
											label="Min supported version"
											fullWidth
											margin="dense"
											autoComplete="off"
											disabled={isUploading}
											error={!!fieldError}
											helperText={
												fieldError?.message ??
												"Devices below this version must update"
											}
										/>
									)}
								/>
							</Grid2>
							<Grid2
								size={{ xs: 12, sm: 6 }}
								sx={{ display: "flex", alignItems: "center" }}
							>
								<Controller
									name="IsActive"
									control={control}
									render={({ field: { value, onChange } }) => (
										<FormControlLabel
											control={
												<Switch
													checked={value}
													onChange={(event) => onChange(event.target.checked)}
													disabled={isUploading}
												/>
											}
											label="Activate now"
										/>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12 }}>
								<Controller
									name="ReleaseNotes"
									control={control}
									render={({ field }) => (
										<TextField
											{...field}
											label="Release notes"
											fullWidth
											multiline
											rows={3}
											margin="dense"
											autoComplete="off"
											disabled={isUploading}
										/>
									)}
								/>
							</Grid2>
							{showBuildWarning && activeRelease ? (
								<Grid2 size={{ xs: 12 }}>
									<Alert severity="warning">
										The build is not above the active release (
										{activeRelease.Version} build {activeRelease.Build}).
										Devices will not see it as an update, and Android refuses to
										install a lower build over a higher one.
									</Alert>
								</Grid2>
							) : null}
							<Grid2 size={{ xs: 12 }}>
								<Alert severity="info">
									Only CI-signed production APKs can update devices in place. An
									APK signed with a different key (for example a debug build)
									will be rejected by Android.
								</Alert>
							</Grid2>
							{isUploading ? (
								<Grid2 size={{ xs: 12 }}>
									<Box display="flex" alignItems="center" gap={1} mt={1}>
										<Box flexGrow={1}>
											<LinearProgress variant="determinate" value={progress} />
										</Box>
										<Typography variant="body2" color="textSecondary">
											{progress < 100 ? `${progress}%` : "Processing..."}
										</Typography>
									</Box>
								</Grid2>
							) : null}
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<Grid2 container spacing={2} justifyContent="flex-end">
							<Grid2 size={{ md: 3, xs: 12 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={onCancel}
									disabled={isUploading}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }}>
								<ButtonWithLoading
									fullWidth
									isLoading={isUploading}
									variant="contained"
									color="primary"
									type="submit"
								>
									Upload
								</ButtonWithLoading>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error In App Releases"
			/>
		</>
	);
};
